import type { I18n } from "@lingui/core"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Plural, Trans } from "@lingui/react/macro"
import { ActionIcon, Anchor, Badge, Button, Collapse, Drawer, Group, Indicator, Stack, Text } from "@mantine/core"
import { Link } from "@tanstack/react-router"
import { useCallback, useState } from "react"
import { formatDateTime } from "../../../../lib/format-date"
import { displayMeetingTitle } from "../../../../lib/meeting-title"
import type { INotificationGroup, INotificationLike, TNotificationSectionId } from "../dedupeNotifications"
import { describeNotification, type IDescribedNotification, type TNotificationLink } from "../notificationCopy"
import type { IProps } from "./interfaces"

import classes from "./styles.module.css"

const TYPE_ICONS: Record<string, string> = {
  budget_exceeded: "✕",
  budget_warning: "!",
  budget_reset: "↺",
  model_deprecated: "⚠",
  analysis_compacted: "⊟",
  analysis_truncated: "⌫",
  "meeting.processed": "✓",
  "meeting.failed": "✕",
}

function severityColor(severity: string): "danger" | "warning" | "info" | "success" {
  if (severity === "error") return "danger"
  if (severity === "warning") return "warning"
  if (severity === "success") return "success"
  return "info"
}

function sectionLabel(id: TNotificationSectionId): React.JSX.Element {
  if (id === "today") return <Trans>Today</Trans>
  if (id === "earlier-this-week") return <Trans>Earlier this week</Trans>
  return <Trans>Older</Trans>
}

function groupTitle(notification: INotificationLike, showModelIds: boolean, i18n: I18n): string {
  const described = describeNotification(notification, { showModelIds })
  if (described.titleMode === "meeting") {
    return displayMeetingTitle(described.subject, undefined, i18n, {
      lite: described.lite,
      startedAt: described.startedAt,
    })
  }
  if (described.titleMode === "model") {
    if (described.modelId) return i18n._(msg`Model no longer available: ${described.modelId}`)
    return i18n._(msg`A model is no longer available`)
  }
  return notification.title
}

function NotificationBody({
  described,
  showModelIds,
}: {
  described: IDescribedNotification
  showModelIds: boolean
}): React.JSX.Element {
  const body = described.body
  if (body.kind === "counts") {
    return (
      <Text size="xs" className={classes.message}>
        <Plural value={body.actionCount} one="# action item" other="# action items" />
        {" · "}
        <Plural value={body.decisionCount} one="# decision" other="# decisions" />
      </Text>
    )
  }
  if (body.kind === "transcript") {
    return (
      <Text size="xs" className={classes.message}>
        <Trans>Transcript ready</Trans>
      </Text>
    )
  }
  if (body.kind === "reason") {
    return (
      <Text size="xs" className={classes.message}>
        {body.reason ?? <Trans>This meeting could not be processed.</Trans>}
      </Text>
    )
  }
  if (body.kind === "ready") {
    return (
      <Text size="xs" className={classes.message}>
        <Trans>Ready to view.</Trans>
      </Text>
    )
  }
  if (body.kind === "model") {
    return (
      <Text size="xs" className={classes.message}>
        {showModelIds ? (
          <Trans>That model is no longer offered. Pick another in Settings.</Trans>
        ) : (
          <Trans>A model this workspace chose is no longer offered. Pick another in Settings.</Trans>
        )}
      </Text>
    )
  }
  return (
    <Text size="xs" className={classes.message}>
      {body.text}
    </Text>
  )
}

function linkLabel(link: TNotificationLink): React.JSX.Element {
  if (link === "try-again") return <Trans>Try again</Trans>
  if (link === "settings") return <Trans>Open settings</Trans>
  return <Trans>Open meeting</Trans>
}

function NotificationAnchor({
  described,
  onNavigate,
  children,
}: {
  described: IDescribedNotification
  onNavigate: () => void
  children: React.ReactNode
}): React.JSX.Element | null {
  if (described.link === "settings") {
    return (
      <Anchor size="xs" onClick={onNavigate} renderRoot={(props) => <Link to="/settings" {...props} />}>
        {children}
      </Anchor>
    )
  }
  if (!described.meetingId) return null
  if (described.link !== "meeting" && described.link !== "try-again") return null
  const id = described.meetingId
  return (
    <Anchor
      size="xs"
      onClick={onNavigate}
      renderRoot={(props) => <Link to="/meetings/$id" params={{ id }} {...props} />}
    >
      {children}
    </Anchor>
  )
}

function NotificationRow({
  group,
  showModelIds,
  expanded,
  onToggle,
  onNavigate,
  onMarkRead,
}: {
  group: INotificationGroup
  showModelIds: boolean
  expanded: boolean
  onToggle: () => void
  onNavigate: () => void
  onMarkRead: (ids: number[]) => void
}): React.JSX.Element {
  const { i18n } = useLingui()
  const newest = group.items[0]
  const described = newest ? describeNotification(newest, { showModelIds }) : null
  const unread = group.unreadCount > 0

  const handleMarkRead = useCallback((): void => {
    onMarkRead(group.unreadIds)
  }, [group.unreadIds, onMarkRead])

  return (
    <Group wrap="nowrap" align="flex-start" className={unread ? `${classes.row} ${classes.rowUnread}` : classes.row}>
      <Badge className={classes.icon} color={severityColor(group.severity)} variant="filled" size="lg" circle>
        {TYPE_ICONS[group.type] ?? "i"}
      </Badge>
      <Stack gap={2} className={classes.body}>
        <Group gap="xs" wrap="nowrap">
          <Text size="sm" fw={unread ? 600 : 400} className={classes.title}>
            {newest ? groupTitle(newest, showModelIds, i18n) : group.title}
          </Text>
          {group.count > 1 && (
            <Badge className={classes.count} size="xs" variant="light" color="info" radius="sm">
              ×{group.count}
            </Badge>
          )}
        </Group>
        {described ? <NotificationBody described={described} showModelIds={showModelIds} /> : null}
        {described?.link ? (
          <NotificationAnchor described={described} onNavigate={onNavigate}>
            {linkLabel(described.link)}
          </NotificationAnchor>
        ) : null}
        <Text size="xs" c="dimmed">
          {group.count > 1 ? (
            <Anchor component="button" type="button" size="xs" onClick={onToggle}>
              <Trans>
                Most recent {formatDateTime(i18n, group.latest)} · {group.count} occurrences
              </Trans>
            </Anchor>
          ) : (
            formatDateTime(i18n, group.latest)
          )}
        </Text>
        <Collapse in={expanded}>
          <Stack gap={4} mt={4}>
            {group.items.map((item) => {
              const itemDescribed = describeNotification(item, { showModelIds })
              const when = formatDateTime(i18n, item.created_at)
              return (
                <Text key={item.id} size="xs">
                  {itemDescribed.link ? (
                    <NotificationAnchor described={itemDescribed} onNavigate={onNavigate}>
                      {when}
                    </NotificationAnchor>
                  ) : (
                    when
                  )}
                </Text>
              )
            })}
          </Stack>
        </Collapse>
      </Stack>
      {unread && (
        <ActionIcon
          className={classes.dismiss}
          variant="subtle"
          size="sm"
          onClick={handleMarkRead}
          title={i18n._(msg`Mark as read`)}
          aria-label={i18n._(msg`Mark as read`)}
        >
          ✓
        </ActionIcon>
      )}
    </Group>
  )
}

export function NotificationBellView({
  unread,
  opened,
  loading,
  sections,
  showModelIds,
  markingAll,
  errorMessage,
  onOpen,
  onClose,
  onMarkRead,
  onMarkAllRead,
}: IProps): React.JSX.Element {
  const { i18n } = useLingui()
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set())

  const toggle = useCallback((key: string): void => {
    setExpandedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  const label = i18n._(msg`Notifications`)
  const badge = unread > 99 ? "99+" : String(unread)

  return (
    <>
      <Indicator
        inline
        label={unread > 0 ? badge : undefined}
        size={16}
        color="danger"
        disabled={unread === 0}
        offset={4}
      >
        <ActionIcon variant="subtle" size="lg" onClick={onOpen} aria-label={label} title={label}>
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <title>{label}</title>
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
        </ActionIcon>
      </Indicator>

      <Drawer opened={opened} onClose={onClose} title={<Trans>Notifications</Trans>} position="right" size="md">
        {unread > 0 && (
          <Stack gap="xs" mb="sm">
            <Button className={classes.markAll} variant="subtle" size="xs" onClick={onMarkAllRead} loading={markingAll}>
              <Trans>Mark all as read</Trans>
            </Button>
          </Stack>
        )}

        {loading && (
          <Text size="sm" c="dimmed" ta="center" py="xl">
            <Trans>Loading…</Trans>
          </Text>
        )}

        {!loading && errorMessage && (
          <Text size="sm" c="dimmed" ta="center" py="xl">
            {errorMessage}
          </Text>
        )}

        {!loading && !errorMessage && sections.length === 0 && (
          <Text size="sm" c="dimmed" ta="center" py="xl">
            <Trans>No notifications</Trans>
          </Text>
        )}

        <Stack gap="md">
          {sections.map((section) => (
            <Stack key={section.id} gap="xs">
              <Text size="xs" fw={600} className={classes.sectionLabel}>
                {sectionLabel(section.id)}
              </Text>
              {section.groups.map((group) => (
                <NotificationRow
                  key={group.key}
                  group={group}
                  showModelIds={showModelIds}
                  expanded={expandedKeys.has(group.key)}
                  onToggle={() => toggle(group.key)}
                  onNavigate={onClose}
                  onMarkRead={onMarkRead}
                />
              ))}
            </Stack>
          ))}
        </Stack>
      </Drawer>
    </>
  )
}
